/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 2 hook(s) need dependency review; fix each and remove this directive */
import { useEffect, useMemo, useRef, useState } from "react";
import CancelIcon from "@Assets/icons/svg/Cancel.svg?react";
import UpdateIcon from "@Assets/icons/svg/Update.svg?react";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import SlickGrid1, { typedColumns } from "@CubeComponents/html/SlickGrid1";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { FormControl, MenuItem, Select } from "@mui/material";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_ERROR } from "@Services/constants";
import { ObjectId } from "bson";
import { portalCreateRoot as createRoot } from "@CubeComponents/html/cellPortals";
import { useTranslation } from "@i18n";
import DownArrowIcon from "@Assets/icons/svg/arrowDown.svg?react";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import { DependencyProps, type ScheduleGridRef } from "../interface";
import Text from "@CubeComponents/html/Text/Text";
import type { RootState } from "@Reducers/index";
import type { DependencyAddEditData, EditorTemplateArgs, TaskLink } from "@WebTypes/scheduleDomain";
import type { Task } from "./dhtmlxgantt";

/**
 * One row of the dependency grid. Rows arrive from three places: links resolved off the selected
 * Gantt task, rows buffered in `dependencyAddEditData`, and the blank row `handleAddClick` seeds
 * (whose ids/lags start as strings). `text` is copied from `name` on edit so the name dropdown can
 * match against it.
 */
type DependencyRow = {
  id?: string | number;
  TaskDisp?: string;
  name?: string;
  text?: string;
  type?: string | number;
  lag?: string | number;
  predId?: string | number;
  successorId?: string | number;
  linkType?: string;
};

/**
 * One entry of the ID / Name dropdowns. The list mixes Gantt tasks with the row being edited, and
 * both templates read only these two fields off either shape.
 */
type DependencyOption = { TaskDisp?: string; text?: string };

function DependencyInGantt(props: Readonly<DependencyProps>) {
  const { t } = useTranslation();
  const {
    currentTab,
    gantt,
    currentSelectedTask,
    deletedLinks,
    setDeletedLinks,
    dependencyAddEditData,
    setDependencyAddEditData,
    hoursPerDay,
  } = props;

  const [dependencyDataset, setDependencyDataset] = useState<DependencyRow[]>([]);
  const [dependencyEditMode, setDependencyEditMode] = useState(false);
  const [dependencyEditItemsData, setDependencyEditItemsData] = useState<DependencyRow>({});
  const [disableEditInDependency, setDisableEditInDependency] = useState(true);
  const [disableDeleteInDependency, setDisableDeleteInDependency] = useState(true);
  const [disableAddInDependency, setDisableAddInDependency] = useState(false);
  const [editClicked, setEditClicked] = useState(false);

  const reactGridRef = useRef<ScheduleGridRef<DependencyRow> | null>(null);
  const dispatch = useDispatch();
  const GanttDetails = useSelector((state: RootState) => state.GanttDetails);
  const slickGridOptions = useMemo(
    () => ({
      forceFitColumns: true,
      enableGridMenu: false,
      enableFiltering: false,
      enableCellMenu: false,
      enableHeaderMenu: false,
    }),
    [],
  );
  const getDependencyType: Record<string | number, string> = {
    0: "Finish - Start",
    1: "Start - Start",
    2: "Finish - Finish",
    3: "Start - Finish",
  };

  const getTaskDependency = () => {
    const selectedId = gantt.getSelectedId();
    const task = gantt.getTask(selectedId);

    if (!selectedId) {
      return {
        predecessor: [],
        successor: [],
        allDependency: [],
      };
    }
    const predecessor = (task.$target || []).map((linkId: string | number) => {
      const link = gantt.getLink(linkId);
      const predecessorTask = gantt.getTask(link.source);
      return {
        id: linkId,
        TaskDisp: predecessorTask.TaskDisp,
        name: predecessorTask.text,
        type: link.type,
        lag: GanttDetails.config?.duration_unit === "hour" ? Math.ceil((link.lag ?? 0) / hoursPerDay) : (link.lag ?? 0),
        predId: link.source,
        linkType: "Predecessor",
      };
    });
    const successor = (task.$source || []).map((linkId: string | number) => {
      const link = gantt.getLink(linkId);
      const succesorTask = gantt.getTask(link.target);
      return {
        id: link.id,
        TaskDisp: succesorTask.TaskDisp,
        name: succesorTask.text,
        type: link.type,
        lag: GanttDetails.config?.duration_unit === "hour" ? Math.ceil((link.lag ?? 0) / hoursPerDay) : (link.lag ?? 0),
        successorId: link.target,
        linkType: "Successor",
      };
    });

    const allDependency = [...predecessor, ...successor];

    return {
      predecessor,
      successor,
      allDependency,
    };
  };

  const getFiltredItems = () => {
    const allTasks = gantt.getTaskByTime();
    // The same local holds first the grouped dependency object and then the flattened row array;
    // both reads are asserted inline rather than restructured.
    let attachedTasks: ReturnType<typeof getTaskDependency> | DependencyRow[] = getTaskDependency();
    attachedTasks = [
      ...(attachedTasks as ReturnType<typeof getTaskDependency>).allDependency,
      ...dependencyAddEditData,
    ];
    return allTasks.filter(
      (task) =>
        task.TaskDisp !== currentSelectedTask.TaskDisp &&
        !(attachedTasks as DependencyRow[]).some(
          (datasetItem: DependencyRow) => datasetItem.TaskDisp === task.TaskDisp,
        ),
    );
  };
  const handleDependencyFields = (field: string, value: string | number) => {
    const tempFields: DependencyRow = { ...dependencyEditItemsData, [field]: value };
    if (field === "TaskDisp" || field === "name") {
      const matchedTasks = gantt.getTaskByTime().find((task: Task) => task[field == "name" ? "text" : field] === value);
      if (matchedTasks) {
        Object.assign(tempFields, {
          TaskDisp: matchedTasks.TaskDisp,
          name: matchedTasks.text,
          id: new ObjectId().toString(),
          type: tempFields.type || "0",
          lag: tempFields.lag || 0,
        });
      }
      // KNOWN BUG, preserved verbatim: these two branches run even when nothing matched, so an
      // unmatched selection dereferences `undefined`. Erased assertions.
      if (currentTab == "Predecessor") {
        tempFields.predId = matchedTasks!.id;
        tempFields.linkType = "Predecessor";
      } else if (currentTab == "Successor") {
        tempFields.successorId = matchedTasks!.id;
        tempFields.linkType = "Successor";
      }
    }
    setDependencyEditItemsData(tempFields);
  };
  const dependencyIdEditorTemplate = () => {
    // On edit the row being edited joins the list so the dropdown can show its current value.
    const dropdownItems: DependencyOption[] = editClicked
      ? [...getFiltredItems(), dependencyEditItemsData]
      : getFiltredItems();
    return (
      <div>
        <FormControl
          fullWidth
          sx={{
            "& .MuiInputBase-root": {
              height: "2rem",
              width: "9.375rem",
              backgroundColor: "white",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#666A84",
            },
            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#6BBBE3",
              borderWidth: "0.0625rem",
            },
          }}
        >
          <Select
            IconComponent={DownArrowIcon}
            onChange={(e) => handleDependencyFields("TaskDisp", e.target.value)}
            value={dependencyEditItemsData.TaskDisp}
            disabled={editClicked}
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: "16.25rem",
                  overflow: "auto",
                  maxWidth: "9.375rem",
                },
              },
              MenuListProps: {
                sx: {
                  py: 0,
                },
              },
            }}
            sx={{
              "& .MuiInputBase-root": {
                height: "2rem",
                py: 0,
              },
              "& .MuiSelect-icon": {
                top: "50%",
                transform: "translateY(-50%)",
                width: "1rem",
                height: "1rem",
              },
            }}
          >
            {dropdownItems.map((item: DependencyOption) => (
              <MenuItem
                key={item.TaskDisp}
                value={item.TaskDisp}
                sx={{
                  borderBottom: "0.125rem solid #ECF3FE",
                  height: "3rem",
                  display: "flex",
                  alignItems: "center",
                  color: "#192048",
                }}
              >
                {item.TaskDisp}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
    );
  };
  const dependencyNameEditorTemplate = () => {
    // On edit the row being edited joins the list so the dropdown can show its current value.
    const dropdownItems: DependencyOption[] = editClicked
      ? [...getFiltredItems(), dependencyEditItemsData]
      : getFiltredItems();
    return (
      <div>
        <FormControl
          fullWidth
          sx={{
            "& .MuiInputBase-root": {
              height: "2rem",
              width: "9.375rem",
              backgroundColor: "white",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#666A84",
            },
            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#6BBBE3",
              borderWidth: "0.0625rem",
            },
          }}
        >
          <Select
            IconComponent={DownArrowIcon}
            onChange={(e) => handleDependencyFields("name", e.target.value)}
            value={dependencyEditItemsData.name}
            disabled={editClicked}
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: "16.25rem",
                  overflow: "auto",
                  maxWidth: "9.375rem",
                },
              },
              MenuListProps: {
                sx: {
                  py: 0,
                },
              },
            }}
            sx={{
              "& .MuiInputBase-root": {
                height: "2rem",
                py: 0,
              },
              "& .MuiSelect-icon": {
                top: "50%",
                transform: "translateY(-50%)",
                width: "1rem",
                height: "1rem",
              },
            }}
          >
            {dropdownItems.map((item: DependencyOption) => {
              return (
                <MenuItem
                  value={item.text}
                  key={item.TaskDisp}
                  sx={{
                    borderBottom: "0.125rem solid #ECF3FE",
                    height: "3rem",
                    display: "flex",
                    alignItems: "center",
                    color: "#192048",
                  }}
                >
                  {item.text}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </div>
    );
  };
  const dependencyTypeEditorTemplate = () => {
    return (
      <div>
        <FormControl
          fullWidth
          sx={{
            "& .MuiInputBase-root": {
              height: "2rem",
              width: "9.375rem",
              backgroundColor: "white",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#666A84",
            },
            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#6BBBE3",
              borderWidth: "0.0625rem",
            },
          }}
        >
          <Select
            IconComponent={DownArrowIcon}
            onChange={(e) => handleDependencyFields("type", e.target.value)}
            value={dependencyEditItemsData.type || "0"}
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: "16.25rem",
                  overflowY: "auto",
                },
              },
              MenuListProps: {
                sx: {
                  py: 0,
                },
              },
            }}
            sx={{
              "& .MuiInputBase-root": {
                height: "2rem",
                py: 0,
              },
              "& .MuiSelect-icon": {
                top: "50%",
                transform: "translateY(-50%)",
                width: "1rem",
                height: "1rem",
              },
            }}
          >
            <MenuItem
              value="0"
              sx={{
                borderBottom: "0.125rem solid #ECF3FE",
                height: "3rem",
                display: "flex",
                alignItems: "center",
                color: "#192048",
              }}
            >
              <Text>{t("Finish - Start")}</Text>
            </MenuItem>
            <MenuItem
              value="1"
              sx={{
                borderBottom: "0.125rem solid #ECF3FE",
                height: "3rem",
                display: "flex",
                alignItems: "center",
                color: "#192048",
              }}
            >
              <Text>{t("Start - Start")}</Text>
            </MenuItem>
            <MenuItem
              value="2"
              sx={{
                borderBottom: "0.125rem solid #ECF3FE",
                height: "3rem",
                display: "flex",
                alignItems: "center",
                color: "#192048",
              }}
            >
              <Text>{t("Finish - Finish")}</Text>
            </MenuItem>
            <MenuItem
              value="3"
              sx={{
                borderBottom: "0.125rem solid #ECF3FE",
                height: "3rem",
                display: "flex",
                alignItems: "center",
                color: "#192048",
              }}
            >
              <Text>{t("Start - Finish")}</Text>
            </MenuItem>
          </Select>
        </FormControl>
      </div>
    );
  };
  const dependencyLagEditorTemplate = (args: EditorTemplateArgs<DependencyRow>) => {
    return (
      <div>
        <TextFieldComponent
          onChange={(e) => handleDependencyFields("lag", e.target.value)}
          type="number"
          defaultValue={args.args.lag || 0}
        />
      </div>
    );
  };

  const typeTemplate = (_row: number, _cell: number, _value: string | number | undefined) => {
    const container = document.createElement("div");
    const root = createRoot(container);
    // The `type` cell is always one of the four link types; an absent value indexes to `undefined`
    // and renders empty, which is the existing behaviour. Erased assertion.
    root.render(<div>{getDependencyType[_value!]}</div>);
    return container;
  };

  const gridColumns = useMemo(
    () =>
      typedColumns<DependencyRow>([
        {
          field: "TaskDisp",
          id: "TaskDisp",
          width: 40,
          textAlign: "Left",
          sortable: true,
          name: t("ID"),
          editorTemplate: dependencyIdEditorTemplate,
        },
        {
          field: "name",
          id: "name",
          width: 50,
          textAlign: "Left",
          sortable: true,
          name: t("Name"),
          editorTemplate: dependencyNameEditorTemplate,
        },
        {
          field: "type",
          id: "type",
          width: 10,
          textAlign: "Left",
          sortable: true,
          name: t("Type"),
          editorTemplate: dependencyTypeEditorTemplate,
          formatter: typeTemplate,
        },
        {
          field: "lag",
          id: "lag",
          width: 10,
          textAlign: "Left",
          sortable: true,
          name: t("Offset"),
          editorTemplate: dependencyLagEditorTemplate,
        },
      ]),

    [dependencyEditItemsData.TaskDisp, dependencyEditItemsData.name, dependencyEditItemsData.type, currentTab],
  );

  useEffect(() => {
    const dependency = getTaskDependency();
    if (currentTab == "Predecessor" || currentTab == "Successor") {
      const deletedIds = new Set(deletedLinks.map((link: DependencyRow) => link.id?.toString()));
      const tempArray: DependencyRow[] = dependencyAddEditData.filter(
        (link: DependencyRow) => link.linkType == currentTab && !deletedIds.has(link.id?.toString()),
      );
      const ganttRows: DependencyRow[] = currentTab == "Predecessor" ? dependency.predecessor : dependency.successor;
      const editedIds = new Set(tempArray.map((link: DependencyRow) => link.id?.toString()));
      const filteredGanttRows = ganttRows.filter((row: DependencyRow) => {
        const rowId = row.id?.toString();
        return !editedIds.has(rowId) && !deletedIds.has(rowId);
      });
      setDependencyDataset([...filteredGanttRows, ...tempArray]);
    }
    setDependencyEditMode(false);
    setDependencyEditItemsData({});
    setEditClicked(false);
    setDisableEditInDependency(true);
    setDisableDeleteInDependency(true);
    setDisableAddInDependency(false);
  }, [currentTab]);

  const setEmptySelectedRows = () => {
    if (reactGridRef.current) {
      reactGridRef.current.dataView.selectedRowIds = [];
      reactGridRef.current.slickGrid.setSelectedRows([]);
      reactGridRef.current.dataView.setSelectedIds([]);
    }
  };

  const handleAddClick = () => {
    // The toolbar only renders with the grid mounted. Erased assertions throughout this file's
    // handlers for the same reason.
    const links = reactGridRef.current!.dataView.items;
    const newRow = {
      id: new ObjectId().toString(),
      TaskDisp: "",
      name: "",
      type: "0",
      lag: 0,
      predId: "",
    };
    const updatedLinks = [newRow, ...links];
    setDependencyDataset(updatedLinks);
    setDependencyEditMode(true);
    setTimeout(() => {
      if (reactGridRef.current?.slickGrid) {
        reactGridRef.current.slickGrid.setSelectedRows([0]);
      }
    }, 0);
  };
  const handleEditClick = () => {
    const selectedRows = reactGridRef.current?.slickGrid.getSelectedRows();
    if (!selectedRows?.length) return;
    const selectedRowdData = reactGridRef.current!.dataView.items[selectedRows[0]!];
    if (!selectedRowdData) return;
    selectedRowdData.text = selectedRowdData.name;
    setDependencyEditItemsData(selectedRowdData);
    setDependencyEditMode(true);
    setEditClicked(true);
  };
  const handleUpdateClick = () => {
    if (dependencyEditItemsData.TaskDisp) {
      const editedId = dependencyEditItemsData.id?.toString();
      setDependencyAddEditData([
        // SHAPE GAP: the rows this grid buffers really do carry `id`/`type`/`lag` as `string | number`
        // and extra display fields (`TaskDisp`/`name`/`text`, which `getFiltredItems` reads back off
        // them), while `DependencyAddEditData` (types/scheduleDomain/types.ts:302) declares the narrow
        // subset. Closing this needs that shared interface widened to what flows through it; it cannot
        // be fixed from this file without dropping fields the same file reads back.
        ...(dependencyAddEditData.filter(
          (link: DependencyRow) => link.id?.toString() !== editedId,
        ) as DependencyAddEditData[]),
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- see SHAPE GAP above: DependencyAddEditData (types/scheduleDomain/types.ts:302) is narrower than the rows this grid buffers; widening it is outside this file
        { ...dependencyEditItemsData } as DependencyRow & DependencyAddEditData,
      ]);
      reactGridRef.current!.dataView.items[reactGridRef.current!.slickGrid.getSelectedRows()[0]!] =
        dependencyEditItemsData;
      reactGridRef.current!.dataView.setItems(reactGridRef.current!.dataView.items);
      setDependencyEditItemsData({});
      setEditClicked(false);
      setDisableEditInDependency(true);
      setDependencyEditMode(false);
      setEmptySelectedRows();
    } else {
      dispatch(
        showToastNotification({
          content: t("Please select a task"),
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
  };
  const handleCancelClick = () => {
    setDependencyEditMode(false);
    if (!editClicked) {
      const remainingItems = reactGridRef.current!.dataView.items.slice(1);
      reactGridRef.current!.dataView.setItems(remainingItems);
      setDependencyDataset(remainingItems);
    }
    setEmptySelectedRows();
    setDisableEditInDependency(true);
    setDisableAddInDependency(false);
    setEditClicked(false);
    setDisableDeleteInDependency(true);
    setDependencyEditItemsData({});
  };
  const handleDeleteClick = () => {
    const selectedRows = reactGridRef.current!.slickGrid.getSelectedRows();
    const selectedItems = selectedRows.map((row: number) => reactGridRef.current!.dataView.items[row]!);
    const allItems = reactGridRef.current!.dataView.items;
    const updatedItems = allItems.filter(
      (item: DependencyRow, index: number) => !selectedRows.some((row: number) => row == index),
    );
    const removedIds = new Set(selectedItems.map((item: DependencyRow) => item.id?.toString()));
    // Same gap in the other direction: `deletedLinks` is declared `TaskLink[]`
    // (components/Schedule/interface.ts:191), which requires `source`/`target` — fields these grid
    // rows have never carried; the only consumer (ActivityInformationModal) reads just `.id`. Needs
    // that shared prop type narrowed to what is actually stored, which is outside this file.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- see comment above: DependencyProps.deletedLinks (components/Schedule/interface.ts:191) declares TaskLink[], whose required source/target these rows do not have
    setDeletedLinks([...deletedLinks, ...(selectedItems as (DependencyRow & TaskLink)[])]);
    setDependencyAddEditData(
      dependencyAddEditData.filter((link: DependencyRow) => !removedIds.has(link.id?.toString())),
    );
    setDependencyDataset(updatedItems);
    setEmptySelectedRows();
  };
  const toolbar = useMemo(
    () => [
      {
        name: t("Add"),
        id: "add",
        prefixIcon: "add",
        cssClass: "font-semibold text-sm",
        buttonType: BUTTON_TYPES.BACKGROUND_BUTTON,
        disabled: disableAddInDependency || dependencyEditMode,
      },
      {
        name: t("Edit"),
        id: "edit",
        prefixIcon: "EditIcon",
        cssClass: "font-semibold text-sm",
        buttonType: BUTTON_TYPES.TEXT_BUTTON,
        disabled: disableEditInDependency,
        hidden: dependencyEditMode,
      },
      {
        name: t("Update"),
        id: "update",
        prefixIconTemplate: <UpdateIcon />,
        cssClass: "font-semibold text-sm",
        buttonType: BUTTON_TYPES.TEXT_BUTTON,
        hidden: !dependencyEditMode,
      },
      {
        name: t("Delete"),
        id: "delete",
        prefixIcon: "delete",
        cssClass: "font-semibold text-sm",
        buttonType: BUTTON_TYPES.TEXT_BUTTON,
        disabled: disableDeleteInDependency,
        hidden: dependencyEditMode,
      },
      {
        name: t("Cancel"),
        id: "cancel",
        prefixIconTemplate: <CancelIcon />,
        cssClass: "font-semibold text-sm",
        buttonType: BUTTON_TYPES.TEXT_BUTTON,
        hidden: !dependencyEditMode,
      },
    ],
    [disableAddInDependency, dependencyEditMode, disableEditInDependency, disableDeleteInDependency],
  );
  const toolbarClickHandler = (args: string) => {
    if (args == "add") {
      handleAddClick();
    } else if (args == "edit") {
      handleEditClick();
    } else if (args == "update") {
      handleUpdateClick();
    } else if (args == "delete") {
      handleDeleteClick();
    } else if (args == "cancel") {
      handleCancelClick();
    }
  };

  const handleRowSelection = () => {
    if (reactGridRef.current) {
      const selectedRows = reactGridRef.current.slickGrid.getSelectedRows();
      setDisableEditInDependency(selectedRows.length !== 1);
      setDisableDeleteInDependency(selectedRows.length < 1);
      setDisableAddInDependency(selectedRows.length > 0);
    }
  };

  const getDependencySlickGrid = () => (
    <div className="flex flex-col overflow-auto h-full ps-3 pe-3 pb-3">
      <SlickGrid1
        toolbar={toolbar}
        toolbarClickHandler={toolbarClickHandler}
        ref={reactGridRef}
        columnDefinitions={gridColumns}
        gridOptions={slickGridOptions}
        dataset={dependencyDataset}
        isEditMode={dependencyEditMode}
        onSelectedRowIdsChanged={handleRowSelection}
      />
    </div>
  );

  return getDependencySlickGrid();
}

export default DependencyInGantt;
